// The live feed, from the client's side.
//
// The server sends `{type, id}` and nothing else, so there is nothing to
// render here: every event is a reason to re-fetch, and this turns it into
// exactly that — a TanStack Query invalidation. The screens stay written the
// way they would be without a stream, which is the point of a feed that
// carries hints rather than data.
//
// `EventSource` cannot send an Authorization header, so a ticket is fetched
// through the ordinary client first and put in the query string. It is
// single-use, so a reconnection asks for another one.

import type { QueryClient } from '@tanstack/react-query';

import { unwrap } from '../api/client';
import { mintStreamTicket } from '../api/gen';
import type { OrgHeader } from '../api/org';

/**
 * Opens the feed and keeps it open, re-connecting with a fresh ticket when it
 * drops. Returns a function that closes it for good.
 */
export function listen(qc: QueryClient, org: OrgHeader): () => void {
  let source: EventSource | null = null;
  let retry: ReturnType<typeof setTimeout> | null = null;
  let closed = false;
  // Back off on repeated failures rather than hammering a server that is
  // restarting, and reset as soon as one connection succeeds.
  let delay = 1000;

  const open = async () => {
    if (closed) return;
    try {
      const ticket = await unwrap(mintStreamTicket({ headers: org }));
      if (closed || !ticket) return;
      source = new EventSource(ticket.url);
      source.onopen = () => {
        delay = 1000;
      };
      // Every event means the same thing: something a screen shows has
      // changed. Which conversation is in the payload, but a list is as
      // likely to be stale as the thread, so both go. The server names its
      // events (`event: conversation`), and a named event never reaches
      // onmessage, so the listener is by name.
      source.addEventListener('conversation', () => {
        void qc.invalidateQueries();
      });
      source.onerror = () => {
        source?.close();
        source = null;
        if (closed) return;
        retry = setTimeout(() => void open(), delay);
        delay = Math.min(delay * 2, 30_000);
      };
    } catch {
      if (closed) return;
      retry = setTimeout(() => void open(), delay);
      delay = Math.min(delay * 2, 30_000);
    }
  };

  void open();

  return () => {
    closed = true;
    if (retry) clearTimeout(retry);
    source?.close();
  };
}
